import { CellContext } from '@tanstack/react-table';
import { CompanyCustomBenefitAssignmentEmployee } from '@pxtra-client-lib-legacy/gen';
import { IconButton, Popper, Box, ClickAwayListener, Divider, Typography } from '@mui/material';
import {
  CalendarScheduleIcon,
  DisabledSquareIcon,
  LimitSquareIcon,
  NoteIcon
} from '../../../../icons';
import { getAssignmentType, getFutureAssignments, getMaximumValidAmount } from '../utils';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Util from '../../../../../misc/util';
import { RoundedPaper } from '../../../atomic/surface';
import { MoneyViewModel } from '../../../../base/viewModels';

const DISABLED_OPACITY = 0.3;

export const HintsCell = ({
  row
}: CellContext<CompanyCustomBenefitAssignmentEmployee, unknown>) => {
  const employee = row.original;
  const { t } = useTranslation();
  const assignmentType = getAssignmentType(employee);
  const maximumValidAmount = getMaximumValidAmount(employee);
  const futureAssignments = getFutureAssignments(employee);
  const isInvalid = ['invalid_global_assignment', 'invalid_individual_assignment'].includes(
    assignmentType
  );

  const [noteAnchor, setNoteAnchor] = useState<HTMLElement | null>(null);
  const [maxValueAnchor, setMaxValueAnchor] = useState<HTMLElement | null>(null);
  const [futureAnchor, setFutureAnchor] = useState<HTMLElement | null>(null);

  const hasNote = !!employee.note;
  const hasMaxValue = maximumValidAmount !== undefined;
  const hasFutureAssignments = futureAssignments.size > 0;
  const isMaxValueAvailable = maximumValidAmount !== 0;

  return (
    <div className="flex items-center flex-nowrap">
      <IconButton
        onMouseOver={(e) => hasNote && setNoteAnchor(e.currentTarget)}
        onMouseLeave={() => setNoteAnchor(null)}
        sx={{
          padding: 0,
          opacity: hasNote ? 1 : DISABLED_OPACITY,
          pointerEvents: hasNote ? 'auto' : 'none'
        }}
      >
        <NoteIcon />
      </IconButton>
      <IconButton
        onMouseOver={(e) => hasMaxValue && setMaxValueAnchor(e.currentTarget)}
        onMouseLeave={() => setMaxValueAnchor(null)}
        sx={{
          padding: 0,
          color: isInvalid ? 'error.main' : 'primary.main',
          opacity: hasMaxValue ? 1 : DISABLED_OPACITY,
          pointerEvents: hasMaxValue ? 'auto' : 'none'
        }}
      >
        {isMaxValueAvailable ? <LimitSquareIcon /> : <DisabledSquareIcon />}
      </IconButton>
      <IconButton
        onMouseOver={(e) => hasFutureAssignments && setFutureAnchor(e.currentTarget)}
        onMouseLeave={() => setFutureAnchor(null)}
        sx={{
          padding: 0,
          opacity: hasFutureAssignments ? 1 : DISABLED_OPACITY,
          pointerEvents: hasFutureAssignments ? 'auto' : 'none'
        }}
      >
        <CalendarScheduleIcon />
      </IconButton>

      {/* Note Overlay */}
      <Popper
        open={!!noteAnchor}
        anchorEl={noteAnchor}
        placement="bottom-end"
        sx={{ zIndex: 1300 }}
      >
        <ClickAwayListener onClickAway={() => setNoteAnchor(null)}>
          <RoundedPaper
            sx={{
              backgroundColor: 'white',
              padding: '16px',
              width: '300px',
              minHeight: '50px',
              display: 'flex',
              border: 'none',
              marginY: '8px'
            }}
            withShadow
          >
            <Box flex="1" paddingRight="16px">
              <Typography
                variant="body2"
                color="black"
                sx={{ paddingBottom: '16px', whiteSpace: 'pre-line' }}
              >
                {employee.note}
              </Typography>
            </Box>
          </RoundedPaper>
        </ClickAwayListener>
      </Popper>

      {/* Max Value Overlay */}
      <Popper
        open={!!maxValueAnchor}
        anchorEl={maxValueAnchor}
        placement="bottom-end"
        sx={{ zIndex: 1300 }}
      >
        <ClickAwayListener onClickAway={() => setMaxValueAnchor(null)}>
          <RoundedPaper
            sx={{
              width: 320,
              height: 'fit-content',
              maxHeight: 420,
              minHeight: 80
            }}
            withShadow
          >
            <Box
              sx={{
                padding: '24px',
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between'
              }}
            >
              {maximumValidAmount === 0 ? (
                <Typography fontWeight="bold">
                  {t('common:MAX_VALUE_HEADER_UNAVAILABLE')}
                </Typography>
              ) : (
                <>
                  <Typography fontWeight="bold">{t('common:MAX_VALUE_HEADER')}</Typography>
                  <Typography
                    sx={{
                      fontWeight: 'bold',
                      alignSelf: 'flex-end',
                      color: '#5F7EFF'
                    }}
                  >
                    {new MoneyViewModel(maximumValidAmount ?? 0).format()}
                  </Typography>
                </>
              )}
            </Box>
            <Divider />
            <Box sx={{ padding: '24px' }}>
              <Typography variant="body2" color="black" sx={{ paddingBottom: '16px' }}>
                {t('common:MAX_VALUE_TEXT_DEFAULT')}
              </Typography>
            </Box>
          </RoundedPaper>
        </ClickAwayListener>
      </Popper>

      {/* Future Assignments Overlay */}
      <Popper
        open={!!futureAnchor}
        anchorEl={futureAnchor}
        placement="bottom-start"
        sx={{ zIndex: 1300 }}
      >
        <ClickAwayListener onClickAway={() => setFutureAnchor(null)}>
          <RoundedPaper
            sx={{
              width: 320,
              height: 'fit-content',
              maxHeight: 420,
              minHeight: 80
            }}
            withShadow
          >
            <Box sx={{ padding: '24px' }}>
              <Typography fontWeight="bold">{t('common:FUTURE_ASSIGNMENTS_HEADER')}</Typography>
            </Box>
            <Divider />
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gridTemplateColumns: '50% 15% 15% 20%',
                gridRowGap: '4px',
                maxHeight: '318px',
                width: '100%'
              }}
            >
              {/* Table header */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'auto 1fr auto',
                  gridAutoRows: '36px',
                  gridColumn: '1 / 3',
                  gridRow: '1 / 2',
                  paddingX: '8px'
                }}
              >
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'inherit',
                    gridColumn: '1 / 3',
                    paddingX: '16px',
                    paddingY: '6px'
                  }}
                >
                  <Typography
                    sx={{
                      gridColumn: '1 / 2',
                      gridRow: '1 / 2',
                      color: 'grey.900',
                      fontSize: '12px',
                      display: 'inline',
                      fontWeight: 'bold',
                      textAlign: 'left'
                    }}
                  >
                    {t('common:MONTH')}
                  </Typography>
                  <Typography
                    sx={{
                      gridColumn: '2 / 3',
                      gridRow: '1 / 2',
                      color: 'grey.900',
                      fontSize: '12px',
                      display: 'inline',
                      fontWeight: 'bold',
                      textAlign: 'right'
                    }}
                  >
                    {t('common:VALUE')}
                  </Typography>
                </Box>
              </Box>
              {/* Table body */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'auto 1fr auto',
                  gridGap: 'inherit',
                  gridAutoRows: '32px',
                  gridColumn: '1 / 3',
                  overflow: 'auto',
                  paddingX: '8px',
                  paddingBottom: '8px',
                  width: '95%'
                }}
              >
                {Array.from(futureAssignments.values()).map((assignment, index, array) => (
                  <Box
                    key={assignment.yearMonth.getTime()}
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: 'inherit',
                      gridColumn: '1 / 3',
                      gridRow: `${index + 1} / ${index + 2}`,
                      backgroundColor: index % 2 === 1 ? 'grey.50' : '',
                      borderRadius: '8px',
                      paddingX: '18px',
                      paddingY: '4px',
                      paddingBottom: index === array.length - 1 ? '8px' : '0'
                    }}
                  >
                    <Typography
                      sx={{
                        gridColumn: '1 / 2',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        fontSize: 'smaller'
                      }}
                    >
                      {assignment.yearMonth?.toLocaleString('default', {
                        month: 'long',
                        year: 'numeric'
                      }) ?? t('common:ERROR_MESSAGE')}
                    </Typography>
                    <Typography
                      align="right"
                      sx={{
                        gridColumn: '2 / 3',
                        fontSize: 'smaller'
                      }}
                    >
                      {Util.formatCurrency(assignment.value)}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </RoundedPaper>
        </ClickAwayListener>
      </Popper>
    </div>
  );
};
